import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Modal from "../Modal";
import { adminApi } from "../../services/api";
import { useToast } from "../Toast";
import { INVITE_STATUS } from "../../utils/constants";

/**
 * Modal for managing invites for a private plan.
 * Shows existing invites and allows creating new ones.
 */
const InviteManager = ({ plan, onClose }) => {
  const toast = useToast();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);

  // Token display after creation (shown once)
  const [createdToken, setCreatedToken] = useState(null);

  const fetchInvites = useCallback(
    async ({ signal } = {}) => {
      try {
        // data is already unwrapped by the axios response interceptor (api.js),
        // so data.invites is a direct property — no .data nesting needed.
        const data = await adminApi.getInvites(plan.id, { signal });
        setInvites(data.invites || []);
      } catch (err) {
        if (err.name === "CanceledError") return;
        toast.error(err.response?.data?.message || "Failed to load invites");
      } finally {
        setLoading(false);
      }
    },
    [plan.id, toast],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchInvites({ signal: controller.signal });
    return () => controller.abort();
  }, [fetchInvites]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setCreating(true);
    setCreatedToken(null);
    try {
      const data = await adminApi.createInvite(plan.id, {
        email: email.trim(),
        expiresInDays,
      });
      const invite = data.invite;
      if (invite.emailSent) {
        toast.success(`Invite sent to ${invite.email}`);
      } else {
        toast.warning(
          `Invite created but email delivery failed. Share the link manually below.`,
        );
      }
      setEmail("");
      setCreatedToken(invite.token);
      await fetchInvites();
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Failed to create invite",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (inviteId) => {
    setRevokingId(inviteId);
    setConfirmRevokeId(null);
    try {
      await adminApi.revokeInvite(inviteId);
      toast.success("Invite revoked");
      await fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to revoke invite");
    } finally {
      setRevokingId(null);
    }
  };

  const frontendUrl =
    process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  const inviteUrl = createdToken
    ? `${frontendUrl}/invite?token=${createdToken}`
    : null;

  const handleCopyUrl = () => {
    navigator.clipboard
      .writeText(inviteUrl)
      .then(() => toast.success("Invite URL copied to clipboard"))
      .catch(() => toast.error("Failed to copy URL"));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  /** Determine display status — detect stale "pending" invites that have actually expired */
  const getDisplayStatus = (invite) => {
    if (
      invite.status === INVITE_STATUS.PENDING &&
      new Date(invite.expiresAt) < new Date()
    ) {
      return INVITE_STATUS.EXPIRED;
    }
    return invite.status;
  };

  return (
    <Modal
      isOpen={!!plan}
      onClose={onClose}
      title={`Invites: ${plan.name}`}
      size="large"
    >
      <div className="admin-invite-manager">
        {/* Create invite form */}
        <form className="admin-invite-form" onSubmit={handleCreate}>
          <div className="admin-form-group">
            <label htmlFor="invite-email">Email address</label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="invite-expiry">Expires in</label>
            <select
              id="invite-expiry"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
            >
              <option value={1}>1 day</option>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={creating || !email.trim()}
          >
            {creating ? "Sending..." : "Send Invite"}
          </button>
        </form>

        {/* Token display (shown once after creation) */}
        {createdToken && (
          <div className="admin-invite-token-display">
            <p>
              Invite created! The token below can be shared manually if the
              email does not arrive:
            </p>
            <div className="admin-invite-token-url">
              <input type="text" readOnly value={inviteUrl} />
              <button
                type="button"
                className="admin-btn admin-btn-edit"
                onClick={handleCopyUrl}
              >
                Copy
              </button>
            </div>
          </div>
        )}

        {/* Invites table */}
        {loading ? (
          <p>Loading invites...</p>
        ) : invites.length === 0 ? (
          <p className="admin-invite-empty">
            No invites yet. Send one above to get started.
          </p>
        ) : (
          <>
            {invites.length >= 200 && (
              <p className="admin-invite-empty" style={{ padding: "8px 0" }}>
                Showing the most recent 200 invites.
              </p>
            )}
            <table className="admin-invite-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => {
                  const displayStatus = getDisplayStatus(invite);
                  return (
                    <tr key={invite.id}>
                      <td>{invite.email}</td>
                      <td>
                        <span
                          className={`admin-invite-status admin-invite-status-${displayStatus}`}
                        >
                          {displayStatus}
                        </span>
                      </td>
                      <td>
                        {invite.createdByUsername ||
                          invite.createdByEmail ||
                          "-"}
                      </td>
                      <td>{formatDate(invite.createdAt)}</td>
                      <td>{formatDate(invite.expiresAt)}</td>
                      <td>
                        {invite.status === INVITE_STATUS.PENDING &&
                          displayStatus === INVITE_STATUS.PENDING &&
                          (confirmRevokeId === invite.id ? (
                            <>
                              <button
                                className="admin-btn admin-btn-delete"
                                onClick={() => handleRevoke(invite.id)}
                                disabled={revokingId === invite.id}
                              >
                                {revokingId === invite.id ? "..." : "Confirm"}
                              </button>
                              <button
                                className="admin-btn"
                                onClick={() => setConfirmRevokeId(null)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              className="admin-btn admin-btn-delete"
                              onClick={() => setConfirmRevokeId(invite.id)}
                              disabled={revokingId === invite.id}
                            >
                              Revoke
                            </button>
                          ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Modal>
  );
};

InviteManager.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default InviteManager;
